import { Link } from 'react-router-dom'
import { ArrowLeft, Shield, Copy, Check } from 'lucide-react'
import { useState } from 'react'
import { PAYMENTS_ENABLED } from '../../lib/config'
import { SUBPROCESSORS, RETENTION_DAYS, SUBPROCESSOR_NOTICE_DAYS } from '../../content/trustFacts'

function CopyBlock({ content }: { content: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="relative bg-paper border-2 border-navy p-4 font-mono text-sm leading-relaxed">
      <button
        onClick={handleCopy}
        className="absolute top-3 right-3 p-1.5 border-2 border-navy/30 hover:border-navy hover:bg-stone transition cursor-pointer"
        title="Copy to clipboard"
      >
        {copied ? <Check size={14} className="text-teal" /> : <Copy size={14} className="text-navy/70" />}
      </button>
      <pre className="whitespace-pre-wrap pr-10">{content}</pre>
    </div>
  )
}

const DOMAINS_LIST = [
  'dayonewithai.com',
  'dayone.mvpclub.ai',
  '*.googleapis.com',
  '*.firebaseapp.com',
  '*.gstatic.com',
  'www.google.com',
  ...(PAYMENTS_ENABLED ? ['*.stripe.com'] : []),
  '*.ingest.us.sentry.io',
].join('\n')

const SPECIFIC_DOMAINS = `# Primary application
dayonewithai.com
www.dayonewithai.com
dayone.mvpclub.ai
day-one-workshop.web.app

# Firebase / Google Cloud (authentication, database, hosting)
identitytoolkit.googleapis.com
securetoken.googleapis.com
firestore.googleapis.com
apis.google.com
day-one-workshop.firebaseapp.com
www.google.com
www.gstatic.com

# Google Fonts (typefaces)
fonts.googleapis.com
fonts.gstatic.com
${PAYMENTS_ENABLED ? `
# Stripe (payment processing)
api.stripe.com
checkout.stripe.com
js.stripe.com
` : ''}
# Sentry (error monitoring)
*.ingest.us.sentry.io`

const CONNECTIONS = [
  {
    domain: 'dayonewithai.com',
    purpose: 'The application itself, plus its backend API endpoints under /api',
    required: 'Required',
  },
  {
    domain: 'identitytoolkit.googleapis.com',
    purpose: 'Firebase Authentication, sign-in and token issuance',
    required: 'Required',
  },
  {
    domain: 'securetoken.googleapis.com',
    purpose: 'Firebase Authentication, session token refresh',
    required: 'Required',
  },
  {
    domain: 'firestore.googleapis.com',
    purpose: 'Firestore database reads and writes, including the wss:// real-time channel',
    required: 'Required',
  },
  {
    domain: 'day-one-workshop.firebaseapp.com',
    purpose: 'Firebase Auth sign-in handler (loaded in a hidden iframe and popup window)',
    required: 'Required',
  },
  {
    domain: 'apis.google.com',
    purpose: 'Google Sign-In client library',
    required: 'Required (Google sign-in)',
  },
  {
    domain: 'www.google.com, www.gstatic.com',
    purpose: 'Static assets loaded by the Google Sign-In flow',
    required: 'Required (Google sign-in)',
  },
  {
    domain: 'fonts.googleapis.com, fonts.gstatic.com',
    purpose: 'Google Fonts stylesheet and webfont files. If blocked, text falls back to system fonts and the app still works.',
    required: 'Optional',
  },
  ...(PAYMENTS_ENABLED
    ? [{
        domain: 'checkout.stripe.com, api.stripe.com, js.stripe.com',
        purpose: 'Stripe Checkout, reached only when a payment is being made',
        required: 'Required (payments)',
      }]
    : []),
  {
    domain: '*.ingest.us.sentry.io',
    purpose: 'Sentry error monitoring, stack traces and browser/OS metadata sent when something breaks. If blocked, the app still works and we simply lose the diagnostic.',
    required: 'Optional',
  },
]

// Subprocessors, retention and the DPA all read app/src/content/trustFacts.ts, so this
// page, /privacy and /dpa cannot drift apart. Add a vendor there, not here.

const CHANGELOG = [
  {
    date: 'September 2026',
    note: 'Published a pre-signed Data Processing Agreement at /dpa on Common Paper Standard Terms v1.1. Corrected the AI path: OpenRouter routing to Modal is primary, Anthropic is the fallback, and no session content reaches Z.ai. Restated retention as 365 days from session creation, matching the scheduled job that enforces it.',
  },
  {
    date: 'August 2026',
    note: 'Content-Security-Policy moved from report-only to enforcing. Published this subprocessor list. Replaced open-ended data retention with a fixed 12-month window and shipped self-serve deletion.',
  },
  {
    date: 'March 2026',
    note: 'Initial publication: domain whitelist, security header baseline, and infrastructure summary.',
  },
]

export default function ITWhitelisting() {
  return (
    <div className="min-h-screen bg-stone font-body text-navy">
      <nav className="sticky top-0 z-40 bg-navy text-stone px-6 py-3 flex items-center justify-between border-b-2 border-golden">
        <Link to="/" className="flex items-center gap-2 hover:text-stone/80 transition">
          <ArrowLeft size={18} />
          <span className="hl text-xl text-stone">Day One</span>
        </Link>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        <div className="flex items-center gap-3 mb-2">
          <Shield size={28} className="text-amber" />
          <h1 className="hl text-[clamp(1.6rem,4.4vw,2.6rem)]">IT &amp; Security Guide</h1>
        </div>
        <p className="descriptor text-navy/75 mb-2">For the person who owns web filtering: what to allow, what we run on, and what we keep.</p>
        <p className="mono-label text-navy/70 mb-10">Last reviewed: September 2026</p>

        <div className="prose prose-navy max-w-none space-y-8 text-navy/80 leading-relaxed">

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">What is Day One?</h2>
            <p>
              Day One is a web-based team onboarding workshop platform built by{' '}
              <strong>MVP Club Consulting</strong> (Denver, CO). It uses AI facilitators to guide
              new teams through structured onboarding sessions in real time. Day One runs entirely
              in the browser. There is no desktop application or browser extension to install.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Quick Domain Whitelist</h2>
            <p>
              To allow access to Day One, add these domains to your web proxy or firewall allow-list.
              All traffic is HTTPS (port 443) only.
            </p>
            <CopyBlock content={DOMAINS_LIST} />
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Detailed Domain Breakdown</h2>
            <p>If your policy requires specific endpoint-level rules rather than wildcard domains:</p>
            <CopyBlock content={SPECIFIC_DOMAINS} />
            <p className="mt-6">
              Every outbound connection the browser makes is listed below. This is enforced in the
              browser by our{' '}
              <code className="bg-paper border border-navy/25 px-1.5 py-0.5 text-sm">Content-Security-Policy</code>{' '}
              header, which blocks requests to any destination not on this list.
            </p>
            <div className="not-prose mt-4 -mx-6 px-6 md:mx-0 md:px-0 overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-navy text-left">
                    <th className="font-display font-normal text-navy py-2 pr-4">Domain</th>
                    <th className="font-display font-normal text-navy py-2 pr-4">Purpose</th>
                    <th className="font-display font-normal text-navy py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {CONNECTIONS.map(row => (
                    <tr key={row.domain} className="border-b border-navy/10 align-top">
                      <td className="py-3 pr-4 font-mono text-xs text-navy leading-relaxed">{row.domain}</td>
                      <td className="py-3 pr-4 text-navy/70 leading-relaxed">{row.purpose}</td>
                      <td className="py-3 text-navy/70 leading-relaxed whitespace-nowrap">{row.required}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">URL Categorization</h2>
            <p>
              If your web filter blocks Day One as "Uncategorized" or "AI/ML," you can submit a
              recategorization request to your filtering vendor. Day One should be categorized as
              {' '}<strong>Business and Economy</strong> or <strong>Computer and Internet / Technology</strong>.
            </p>
            <p>Common vendor portals for recategorization:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Zscaler:</strong> sitereview.zscaler.com</li>
              <li><strong>Palo Alto (PAN-DB):</strong> urlfiltering.paloaltonetworks.com</li>
              <li><strong>Cisco Umbrella:</strong> talosintelligence.com</li>
              <li><strong>BrightCloud / Webroot:</strong> brightcloud.com/tools/change-request.php</li>
              <li><strong>Fortinet:</strong> fortiguard.com/webfilter</li>
              <li><strong>Symantec / Norton:</strong> sitereview.norton.com</li>
              <li><strong>Forcepoint:</strong> csi.forcepoint.com</li>
            </ul>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Security Posture</h2>

            <h3 className="font-display text-lg font-semibold text-navy mt-6 mb-2">Infrastructure</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Hosted on <strong>Google Cloud Platform</strong> via Firebase: data at rest in us-east1, compute in us-central1, both United States, with no cross-border transfer</li>
              <li>All data encrypted at rest (AES-256) and in transit (TLS 1.2+)</li>
              <li>HTTPS enforced with HSTS preload</li>
              <li>No self-hosted servers, fully managed GCP infrastructure</li>
            </ul>

            <h3 className="font-display text-lg font-semibold text-navy mt-6 mb-2">Authentication</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Powered by <strong>Firebase Authentication</strong> (Google Identity Platform)</li>
              <li>Supports Google Sign-In and email/password sign-in. Starting a session requires a signed-in account with a verified email address</li>
              <li>Session tokens managed by Firebase, no custom credential storage</li>
            </ul>

            <h3 className="font-display text-lg font-semibold text-navy mt-6 mb-2">Data Handling</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Primary AI path: OpenRouter, Inc. to Modal.</strong> Every workshop turn goes to OpenRouter, which routes it to <strong>Modal</strong>, the US inference host we pin for the GLM model we use. OpenRouter does not store prompts or completions by default and does not train on them; the data policy OpenRouter publishes for the Modal endpoint is zero prompt retention and no training. Z.ai's own endpoint is on our ignore list, so no session content is sent to Z.ai the company</li>
              <li><strong>Fallback AI path: Anthropic's Claude API.</strong> Used only when the primary path fails or is too slow. Anthropic does not train on API-submitted data and deletes API inputs and outputs within 30 days by default. Anthropic's web search tool is attached only on this path, and it sends a short query derived from the conversation, never the transcript or an uploaded document. OpenRouter web search is switched off</li>
              {PAYMENTS_ENABLED ? (
                <li>Payment processing via <strong>Stripe</strong>. PCI-DSS Level 1 compliant. No payment credentials are stored on our servers</li>
              ) : (
                <li>No payment data is collected. Day One is free, so there is no card number, billing address, or payment processor involved</li>
              )}
              <li>Error monitoring via <strong>Sentry</strong>: technical error data only. No advertising SDKs, behavioral analytics, or tracking pixels</li>
              <li>No cookies beyond essential Firebase authentication tokens</li>
              <li>Server-side credentials (AI, payment, and email API keys) are held in <strong>Google Secret Manager</strong>, injected into functions at runtime, and never exposed to the client</li>
              <li>All data is stored in the United States. There is no cross-border transfer or offshore processing</li>
            </ul>

            <h3 className="font-display text-lg font-semibold text-navy mt-6 mb-2">Security Headers</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li><code className="bg-paper border border-navy/25 px-1.5 py-0.5 text-sm">Strict-Transport-Security</code>: HSTS with preload</li>
              <li><code className="bg-paper border border-navy/25 px-1.5 py-0.5 text-sm">X-Content-Type-Options: nosniff</code></li>
              <li><code className="bg-paper border border-navy/25 px-1.5 py-0.5 text-sm">X-Frame-Options: SAMEORIGIN</code></li>
              <li><code className="bg-paper border border-navy/25 px-1.5 py-0.5 text-sm">Referrer-Policy: strict-origin-when-cross-origin</code></li>
              <li><code className="bg-paper border border-navy/25 px-1.5 py-0.5 text-sm">Permissions-Policy</code>: camera, microphone, and geolocation disabled</li>
              <li><code className="bg-paper border border-navy/25 px-1.5 py-0.5 text-sm">Content-Security-Policy</code>: enforced (not report-only). Restricts scripts, styles, frames, and network destinations to the allow-list above; no inline scripts, no <code className="bg-paper border border-navy/25 px-1.5 py-0.5 text-sm">eval</code>, no plugin content</li>
            </ul>
            <p className="mt-4 text-sm">
              You can verify all of the above yourself by inspecting the response headers on any page
              of this site. No account required.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Subprocessors</h2>
            <p>
              These are every third party that processes customer data on our behalf. We do not sell
              data, and no subprocessor receives it for advertising or model-training purposes.
            </p>
            <div className="not-prose mt-4 -mx-6 px-6 md:mx-0 md:px-0 overflow-x-auto">
              <table className="w-full min-w-[860px] text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-navy text-left">
                    <th className="font-display font-normal text-navy py-2 pr-4">Subprocessor</th>
                    <th className="font-display font-normal text-navy py-2 pr-4">Purpose</th>
                    <th className="font-display font-normal text-navy py-2 pr-4">Data it processes</th>
                    <th className="font-display font-normal text-navy py-2 pr-4">What it retains</th>
                    <th className="font-display font-normal text-navy py-2">Region</th>
                  </tr>
                </thead>
                <tbody>
                  {SUBPROCESSORS.map(row => (
                    <tr key={row.name} className="border-b border-navy/10 align-top">
                      <td className="py-3 pr-4 leading-relaxed">
                        <a href={row.policyUrl} target="_blank" rel="noopener noreferrer" className="text-navy font-semibold underline decoration-navy/20 hover:decoration-navy">
                          {row.name}
                        </a>
                        <span className="block text-xs text-navy/70 mt-1">{row.role}</span>
                      </td>
                      <td className="py-3 pr-4 text-navy/70 leading-relaxed">{row.purpose}</td>
                      <td className="py-3 pr-4 text-navy/70 leading-relaxed">{row.data}</td>
                      <td className="py-3 pr-4 text-navy/70 leading-relaxed">{row.retention}</td>
                      <td className="py-3 text-navy/70 leading-relaxed">{row.location}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm">
              We notify customers by email at least {SUBPROCESSOR_NOTICE_DAYS} days before a new
              subprocessor starts processing customer data. To be added to that list, email{' '}
              <a href="mailto:security@mvpclub.ai" className="text-amber hover:text-amber-dark underline">
                security@mvpclub.ai
              </a>. The same list, with the notice and objection terms around it, is part of our{' '}
              <Link to="/dpa" className="text-amber hover:text-amber-dark underline">
                Data Processing Agreement
              </Link>.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Data Retention &amp; Deletion</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Session data</strong> (chat transcripts, uploaded document text, reflections, and generated artifacts) is retained for <strong>{RETENTION_DAYS} days from the day the session is created</strong>, then deleted by a scheduled job that runs daily.</li>
              <li><strong>Account records</strong>, meaning name and email address, are kept while the account is active.</li>
              <li><strong>Self-serve deletion:</strong> users delete their own sessions and account data from within the app at any time, without contacting us.</li>
              <li><strong>Deletion requests</strong> sent to <a href="mailto:privacy@mvpclub.ai" className="text-amber hover:text-amber-dark underline">privacy@mvpclub.ai</a> are honored within <strong>30 days</strong>.</li>
              <li>OpenRouter and Modal, the primary AI path, hold no prompts or completions by default. Anthropic, the fallback path, deletes API inputs and outputs within 30 days by default. Neither path trains on the data.</li>
              <li>The full data processing terms, including deletion on request and the subprocessor notice period, are in our <Link to="/dpa" className="text-amber hover:text-amber-dark underline">Data Processing Agreement</Link>, published pre-signed.</li>
              {PAYMENTS_ENABLED && (
                <li>Stripe retains billing records independently as required by financial regulation.</li>
              )}
            </ul>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">What Day One Does NOT Do</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>Does not require a browser extension or desktop install</li>
              <li>Does not access the local file system (beyond standard browser file upload dialogs)</li>
              <li>Does not use WebRTC, screen sharing, or camera/microphone access</li>
              <li>Does not set third-party cookies or tracking pixels</li>
              <li>Does not contact advertising networks, data brokers, or behavioral analytics services</li>
              <li>In the browser, communicates only with the domains listed above, and the enforced Content-Security-Policy blocks anything else. Server-side, our Cloud Functions additionally reach OpenRouter, Anthropic{PAYMENTS_ENABLED ? ', Stripe' : ''} and Resend, all listed as subprocessors below</li>
              <li>Does not require VPN or special network configuration</li>
            </ul>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Network Requirements</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Protocol:</strong> HTTPS only (port 443)</li>
              <li><strong>WebSocket:</strong> Required for real-time Firestore updates (wss:// to firestore.googleapis.com)</li>
              <li><strong>Bandwidth:</strong> Minimal (text-based application with occasional small file uploads)</li>
              <li><strong>Browser support:</strong> Any modern browser (Chrome, Edge, Firefox, Safari)</li>
            </ul>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">SSL/TLS Inspection Note</h2>
            <p>
              If your organization uses SSL/TLS inspection (MITM proxy), Firebase real-time connections
              may fail due to certificate pinning. If users experience connection errors, consider
              bypassing SSL inspection for <code className="bg-paper border border-navy/25 px-1.5 py-0.5 text-sm">*.googleapis.com</code> and{' '}
              <code className="bg-paper border border-navy/25 px-1.5 py-0.5 text-sm">*.firebaseapp.com</code>.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Contact</h2>
            <p>
              Our{' '}
              <Link to="/dpa" className="text-amber hover:text-amber-dark underline">
                Data Processing Agreement
              </Link>{' '}
              is published pre-signed at <code className="bg-paper border border-navy/25 px-1.5 py-0.5 text-sm">/dpa</code>,
              so there is nothing to request and nothing to wait on. For security questionnaire responses or
              any other documentation, contact us:
            </p>
            <ul className="list-none pl-0 space-y-1 mt-3">
              <li><strong>Security:</strong>{' '}
                <a href="mailto:security@mvpclub.ai" className="text-amber hover:text-amber-dark underline">
                  security@mvpclub.ai
                </a>
              </li>
              <li><strong>General:</strong>{' '}
                <a href="mailto:hello@mvpclub.ai" className="text-amber hover:text-amber-dark underline">
                  hello@mvpclub.ai
                </a>
              </li>
            </ul>
            <p className="text-sm text-navy/70 mt-4">
              MVP Club Consulting<br />
              Denver, CO
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Document History</h2>
            <p>
              This page is maintained as a living document and reviewed at least twice a year, and
              again whenever we change infrastructure, add a subprocessor, or adjust retention.
            </p>
            <ul className="list-none pl-0 space-y-3 mt-4">
              {CHANGELOG.map(entry => (
                <li key={entry.date} className="border-l-2 border-amber/40 pl-4">
                  <span className="block text-sm font-semibold text-navy">{entry.date}</span>
                  <span className="block text-sm text-navy/70">{entry.note}</span>
                </li>
              ))}
            </ul>
          </section>

        </div>
      </div>
    </div>
  )
}
